import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useForm} from 'react-hook-form';
import {RESOURCES as resourcesUser} from '../../resourcesUser';
import {makePostDataRequest} from '../../utils/postRequest';
import BaseInput from '../fields/BaseInput';
import NumberInput from '../fields/NumberInput';
import FormError from './FormError';
import LoadingIndicator from '../LoadingIndicator';
import {makeGetDataRequest} from '../../utils/getRequest';

/**
 * Form: Edit Event Configuration for specified User
 *
 * @returns {JSX.Element}
 * @constructor
 */
const FormEventConfigUser = (props) => {

    const eventConfig = props.event_config;

    const {register, errors, formState, handleSubmit, getValues, setValue} = useForm({mode: 'onBlur'});

    const [user, setUser] = useState(null);
    const [userName, setUserName] = useState(null);
    const [userMessage, setUserMessage] = useState(null);
    const [successState, updateSuccessState] = useState(
        {
            message: '',
            success: false
        }
    );
    const [formStageState, setFormStageState] = useState('form');

    const setDefaultConfigMessage = (type) => {
        if (eventConfig[type]) {
            return ' The default is ' + eventConfig[type] + '.';
        }
        return '';
    };

    const handleUserInput = (e) => {
        setUserName(e?.value ?? null);
        setUser(null);
        setUserMessage(null);
    };

    const getUser = () => {
        const request = makeGetDataRequest(resourcesUser, 'UserInfo', 'GET_ONE', {username: userName});
        request.then(result => {
            if (result) {
                if (result.error) {
                    setUser(null);
                    setUserMessage('A user with the username \'' + userName + '\' was not found.');
                } else if (result.username) {
                    // Set user Configurations to defaults if the user does not already override them
                    let userConfig = {
                        max_events: result?.max_events ?? props.event_config.max_events,
                        max_event_shares: result?.max_event_shares ?? props.event_config.max_shares
                    };
                    setValue('max_events', userConfig.max_events);
                    setValue('max_event_shares', userConfig.max_event_shares);
                    setUser(result);
                    setUserMessage(null);
                }

                return Promise.resolve(result);
            } else {
                console.log('User Info Error: No result');
                setUserMessage('A user with the username ' + userName + ' was not found.');
                return Promise.reject('User Info failed');
            }
        }).catch(error => {
            console.log('User Info Error: ', error);
            setUserMessage('A user with the username ' + userName + ' was not found.');
            return Promise.reject('Server Error');
        });
    };

    // Determine if form is incomplete for disabling specific elements
    const disableSubmit = () => {
        return !!(
            !formState.isDirty ||
            formStageState === 'saving' ||
            (errors &&
                (
                    errors.max_events ||
                    errors.max_event_shares
                ))
        );
    };

    // Handle submission event
    const onSubmit = (data) => {
        if (data) {
            const userObj = JSON.parse(JSON.stringify(user));
            const max_events = parseInt(getValues('max_events'), 10);
            const max_event_shares = parseInt(getValues('max_event_shares'), 10);
            // Nullify Configuration values that equal the default
            userObj.max_events = (max_events !== eventConfig.max_events) ? max_events : null;
            userObj.max_event_shares = (max_event_shares !== eventConfig.max_event_shares) ? max_event_shares : null;

            // Submit form to API
            setFormStageState('saving');
            submitForm(userObj);
        }
    };

    // Handle API call and post-submission
    const submitForm = (data) => {
        const request = makePostDataRequest(resourcesUser, 'User', 'UPDATE', data);
        request.then(result => {
            if (result && result.username) {
                updateSuccessState({success: true, message: ''});
                setFormStageState('success');
                return Promise.resolve(result);
            } else {
                updateSuccessState({success: false, message: 'Sorry, there was an error updating this user\'s Personal Event Configuration.'});
                setFormStageState('fail');
                return Promise.resolve();
            }
        }).catch(error => {
            console.log('User Personal Event Configuration Error: ', error);
            updateSuccessState({
                success: false,
                message: 'Sorry, there was an error updating this user\'s Personal Event Configuration: ' + error
            });
            setFormStageState('fail');
            return Promise.reject('Server Error');
        });
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="xrathus-detail">

                    <div className="item-content full">
                        <div className="tab-content active">
                            <p className="instructions">
                                Override Event Configuration values for a specific user. Find a User by username.
                            </p>

                            {/* Find User */}

                            <div className="row">
                                <div className="form-group col col-xs-6">
                                    <BaseInput
                                        type="text"
                                        id="username"
                                        name="username"
                                        label=""
                                        placeholder="Enter a Username"
                                        changeFunc={(e) => handleUserInput(e)}
                                        className="form-input"
                                    >
                                    </BaseInput><br/>
                                    {userMessage &&
                                    <p className="message">
                                        {userMessage}
                                    </p>
                                    }
                                </div>
                                <div className="form-group col col-xs-6">
                                    <button type="button" className="btn btn-primary"
                                        onClick={() => getUser()}
                                        disabled={(!userName)}
                                    >
                                        Find User
                                    </button>
                                </div>
                            </div>

                            {/* Form Inputs */}

                            <div className={`row ${(user) ? 'active' : 'hidden'}`}>
                                <div className="form-group col col-xs-12 col-md-6">
                                    <NumberInput
                                        id="config-max-events"
                                        name="max_events"
                                        label="Max Number of Events"
                                        instructions={`The maximum number of Personal Events this user may create.${setDefaultConfigMessage('max_events')}`}
                                        min={0}
                                        max={100}
                                        step={1}
                                        aria-describedby="error-max-events-required"
                                        aria-invalid={errors.max_events ? 'true' : 'false'}
                                        ref={register({
                                            required: (<FormError id="required" text="A value is required"/>)
                                        })}
                                        className={`form-input ${errors.max_events ? 'input-error' : ''}`}
                                    >
                                        {errors.max_events && errors.max_events.message}
                                    </NumberInput>
                                </div>
                                <div className="form-group col col-xs-12 col-md-6">
                                    <NumberInput
                                        id="config-max-event-shares"
                                        name="max_event_shares"
                                        label="Max Number of Shares"
                                        instructions={`The maximum number of times this user may share an Event with other users.${setDefaultConfigMessage('max_event_shares')}`}
                                        min={0}
                                        max={1000}
                                        step={1}
                                        aria-describedby="error-max_event_shares-required"
                                        aria-invalid={errors.max_event_shares ? 'true' : 'false'}
                                        ref={register({
                                            required: (<FormError id="required" text="A value is required"/>)
                                        })}
                                        className={`form-input ${errors.max_event_shares ? 'input-error' : ''}`}
                                    >
                                        {errors.max_event_shares && errors.max_event_shares.message}
                                    </NumberInput>
                                </div>
                            </div>
                        </div>

                        {/* Messaging and Actions */}

                        <div className={`form-btns right ${(user) ? 'active' : 'hidden'}`}>
                            {formStageState === 'saving' &&
                            <>
                                <LoadingIndicator color="dark" centered={false} active={true}/>
                                <p className="message">Saving User Configuration...</p>
                            </>
                            }
                            {formStageState === 'success' &&
                            <p className="message">User Event Configuration saved</p>
                            }
                            {formStageState === 'fail' &&
                            <>
                                {successState.message !== '' &&
                                <p className="message">
                                    {successState.message}
                                </p>
                                }
                                {successState.message === '' &&
                                <p className="message">
                                    Sorry, there was an issue saving this user&apos;s Event Configuration.
                                </p>
                                }
                            </>
                            }

                            <button type="submit" className="btn btn-primary" disabled={!!disableSubmit()}>
                                Save User Configuration
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        event_config: state.event_config
    };
};

FormEventConfigUser.propTypes = {
    event_config: PropTypes.any,
};

export default connect(
    mapStateToProps
)(FormEventConfigUser);
